import { useCreateCalendarEventMutation, useRemoveCalendarEventMutation } from '@/queries/calendar-event.queries';
import timezoneService from '@/services/timezone.service';
import uuidService from '@/services/uuid.service';
import { getZonedDateTimeFromLocaleString } from '@/timetime/timetime';
import { IInvitation } from '@/types/calendar-event/IAttendee';
import { ICalendarEvent } from '@/types/calendar-event/ICalendarEvent';
import { t } from 'i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';

interface IArgs {
	event: ICalendarEvent;
	calendarId: string;
	onClose: () => void;
}

export function useEventDetailsFormController(args: IArgs) {
	const form = useForm({ defaultValues: args.event });

	const formParticipant = useForm({
		defaultValues: {
			email: '',
			displayTimeMode: 'EXACT' as IInvitation['displayTimeMode'],
			summary: t('You have been invited to an event'),
			subtitle: t('Please accept or decline the invitation.'),
			description: '',
		},
	});

	const { remove, append, fields } = useFieldArray({
		control: form.control,
		name: 'attendees',
	});

	const editCalendarEventMutation = useCreateCalendarEventMutation();
	const removeCalendarEventMutation = useRemoveCalendarEventMutation();

	const handleSubmit = form.handleSubmit((values) => {
		// Transform the date input into a valid value for the backend
		if (values.interval?.inclusiveStart?.dateTime) {
			values.interval.inclusiveStart = _localDateTimeToISODateTime(
				values.interval.inclusiveStart.dateTime,
				timezoneService.getTimeZone()
			);
		}

		// Transform the date input into a valid value for the backend
		if (values.interval?.exclusiveEnd?.dateTime) {
			values.interval.exclusiveEnd = _localDateTimeToISODateTime(
				values.interval.exclusiveEnd.dateTime,
				timezoneService.getTimeZone()
			);
		}

		return editCalendarEventMutation.mutateAsync({ ...values, calendarId: args.calendarId });
	});

	const handleFormParticipantSubmit = formParticipant.handleSubmit((values) => {
		append({
			email: values.email,
			id: uuidService.v4(),
			status: 'NEEDS_ACTION',
			invitation: {
				id: uuidService.v4(),
				displayTimeMode: values.displayTimeMode,
				summary: values.summary,
				subtitle: values.subtitle,
				description: values.description,
			},
		});
		handleSubmit();
		formParticipant.reset();
	});

	function handleRemoveParticipantClick(index: number) {
		remove(index);
		handleSubmit();
	}

	function handleDeleteEventClick() {
		return removeCalendarEventMutation.mutateAsync({ id: args.event.id }).then(args.onClose);
	}

	return {
		remove,
		handleSubmit,
		handleFormParticipantSubmit,
		handleRemoveParticipantClick,
		handleDeleteEventClick,
		fields,
		form,
		formParticipant,
		// Transform this into values suitable for the datetime-local
		from: _isoDateTimeToLocalDateTime(
			form.watch('interval.inclusiveStart.dateTime'),
			form.watch('interval.inclusiveStart.timeZone')
		),
		to: _isoDateTimeToLocalDateTime(
			form.watch('interval.exclusiveEnd.dateTime'),
			form.watch('interval.exclusiveEnd.timeZone')
		),
	};
}

/**
 * Transform an ISO date with timezone and offset to just a local dateTime.
 */
function _isoDateTimeToLocalDateTime(zonedDateTimeStr: string, timeZone: string | null): string {
	if (!zonedDateTimeStr) {
		return '';
	}

	timeZone = timeZone || timezoneService.getTimeZone();
	const zonedDateTime = Temporal.ZonedDateTime.from(`${zonedDateTimeStr}[${timeZone}]`);

	return zonedDateTime.toPlainDateTime().toString({ smallestUnit: 'minute' });
}

function _localDateTimeToISODateTime(localDateTime: string, timeZone: string): { dateTime: string; timeZone: string } {
	return {
		dateTime: getZonedDateTimeFromLocaleString(localDateTime, timeZone).toString().split('[')[0],
		timeZone,
	};
}
